// /src/utils/Onboarding/SelfieSegmentation.js

import { ImageSegmenter, FilesetResolver } from '@mediapipe/tasks-vision';

/**
 * MEDIAPIPE SELFIE SEGMENTATION (BACKGROUND)
 *
 * - Initializes and runs the MediaPipe ImageSegmenter with the Selfie Segmenter model.
 * - Returns a category mask (0 = background, 1 = person).
 * - Provides a helper to build an offscreen background-only overlay canvas.
 */

let selfieSegmenter = null;
let isInitializing = false;
let initError = null;

export async function initializeSelfieSegmenter() {
  if (selfieSegmenter) return selfieSegmenter;

  if (isInitializing) {
    while (isInitializing) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 50));
    }
    return selfieSegmenter;
  }

  try {
    isInitializing = true;

    // Suppress WASM internal logs during initialization
    const originalLog = console.log;
    const originalWarn = console.warn;
    console.log = (...args) => {
      const msg = args[0];
      if (typeof msg === 'string' && (msg.includes('GL version') || msg.includes('Graph successfully') || msg.includes('gl_context') || msg.includes('segmentation_postprocessor'))) return;
      originalLog.apply(console, args);
    };
    console.warn = (...args) => {
      const msg = args[0];
      if (typeof msg === 'string' && (msg.includes('OpenGL error checking') || msg.includes('gl_context'))) return;
      originalWarn.apply(console, args);
    };

    const vision = await FilesetResolver.forVisionTasks(
      'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm'
    );

    selfieSegmenter = await ImageSegmenter.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath:
          'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite',
        delegate: 'GPU',
      },
      runningMode: 'IMAGE',
      outputCategoryMask: true,
      outputConfidenceMasks: false,
    });

    // Restore console
    console.log = originalLog;
    console.warn = originalWarn;

    initError = null;
    return selfieSegmenter;
  } catch (e) {
    console.error('[SelfieSeg] Failed to initialize:', e);
    initError = e;
    selfieSegmenter = null;
    throw e;
  } finally {
    isInitializing = false;
  }
}

export async function segmentSelfie(imageElement) {
  if (!selfieSegmenter) {
    await initializeSelfieSegmenter();
  }
  if (!selfieSegmenter) throw new Error('Selfie Segmenter unavailable');

  if (!imageElement || !imageElement.complete) {
    throw new Error('Invalid or incomplete image element');
  }

  const result = await selfieSegmenter.segment(imageElement);
  if (!result || !result.categoryMask) {
    console.warn('[SelfieSeg] No category mask returned.');
    return null;
  }
  return {
    maskType: 'category',
    categoryMask: result.categoryMask,
  };
}

export function buildBackgroundOverlayCanvas(maskType, categoryMask, rgba = [255, 255, 255, 160], alphaScale = 1) {
  if (maskType !== 'category' || !categoryMask) return null;

  const width = categoryMask.width;
  const height = categoryMask.height;
  if (!width || !height) return null;

  const labels = categoryMask.getAsUint8Array();
  if (!labels || labels.length !== width * height) return null;

  const [r, g, b, a] = rgba;
  const out = new ImageData(width, height);
  const data = out.data;

  // 0 = background, 1 = person
  const finalA = Math.max(0, Math.min(255, Math.round((a ?? 160) * (alphaScale ?? 1))));
  for (let i = 0; i < labels.length; i++) {
    const isBg = labels[i] === 0;
    const j = i * 4;
    if (isBg) {
      data[j] = r;
      data[j + 1] = g;
      data[j + 2] = b;
      data[j + 3] = finalA;
    } else {
      data[j] = 0;
      data[j + 1] = 0;
      data[j + 2] = 0;
      data[j + 3] = 0;
    }
  }

  const off = document.createElement('canvas');
  off.width = width;
  off.height = height;
  const octx = off.getContext('2d');
  octx.putImageData(out, 0, 0);

  return off;
}

export function cleanupSelfieSegmenter() {
  if (selfieSegmenter) {
    try {
      selfieSegmenter.close();
    } catch (e) {
      console.warn('[SelfieSeg] Close error:', e);
    }
    selfieSegmenter = null;
  }
  initError = null;
  isInitializing = false;
}
